/**
 * worksheetExportApi — 학습지 outline 을 편집 가능한 워드(.docx)/한글(.hwpx)로 내보내기.
 *
 * 백엔드: POST /api/worksheet/export (docs/worksheet_export_docx_hwpx_wp_g_2026_09.md)
 *   요청 { outline, format:'docx'|'hwpx', include_answer_guide, columns?, paper_style?, filename?, conversation_id?, outline_version_id?, canvas_version_id? }
 *   응답 = 산출물 레코드 모양(camelCase) + 부가 정보(artifactId, editorPath, previewUrls, warnings ...)
 *
 * 미리보기 헤더(텍스트형)에서 한글·워드를 연다. 이미지형은 UI에서 PDF만 보여 이 API를 안 부른다.
 * isWorksheetExportAvailable() 은 결과 카드 포맷 줄용 플래그로 남겨 둔다.
 */

import { getAuth } from 'firebase/auth';
import { getApiBaseUrl } from '../utils/apiConfig';
import { fetchWithRetry, calcApiTimeout } from '../utils/fetchWithRetry';
import { buildArtifactRecord } from '../utils/artifactRecords';
import { isWorksheetExportEnabled } from '../utils/artifactHistory';
import { resolveWorksheetExportLayout } from '../utils/worksheetPaperFormat';
import { koreanOrNull } from '../utils/koreanMessage';

export { resolveWorksheetExportLayout } from '../utils/worksheetPaperFormat';

export const WORKSHEET_EXPORT_FORMATS = Object.freeze(['docx', 'hwpx']);

export class WorksheetExportError extends Error {
  constructor(message, { status = null, code = null } = {}) {
    super(message);
    this.name = 'WorksheetExportError';
    this.status = status;
    this.code = code;
  }
}

export const isWorksheetExportAvailable = () => isWorksheetExportEnabled();

const buildHeaders = async () => {
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  try {
    const user = getAuth().currentUser;
    if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;
  } catch (_e) {
    // 비로그인·인증 초기화 전 — 익명 경로로 진행
  }
  return headers;
};

const messageForStatus = (status, detail) => {
  const safeDetail = koreanOrNull(detail);
  if (status === 422) return safeDetail || '학습지 내용이 비어 있어 파일로 만들 수 없어요';
  if (status === 504) return '파일을 만드는 데 시간이 오래 걸려요. 잠시 후 다시 시도해주세요';
  if (status === 503) return '파일 저장소 연결이 잠시 불안정해요. 잠시 후 다시 시도해주세요';
  return safeDetail || '파일을 만들지 못했어요. 다시 시도해주세요';
};

/**
 * @param {{
 *   outline: object, format: 'docx'|'hwpx', includeAnswerGuide?: boolean (기본 true), columns?: 1|2,
 *   paperStyle?: 'school'|'csat'|null, filename?: string|null, conversationId?: string|null,
 *   outlineVersionId?: string|null, canvasVersionId?: string|null,
 *   previewPages?: number, signal?: AbortSignal,
 * }} params
 * @returns {Promise<{ record: object, raw: object }>} record 는 buildArtifactRecord 로 검증한 산출물 레코드
 */
export async function exportWorksheet({
  outline,
  format,
  includeAnswerGuide = true,
  columns,
  paperStyle,
  filename = null,
  conversationId = null,
  outlineVersionId = null,
  canvasVersionId = null,
  previewPages = 1,
  signal,
} = {}) {
  const normalizedFormat = String(format || '').toLowerCase();
  if (!WORKSHEET_EXPORT_FORMATS.includes(normalizedFormat)) {
    throw new WorksheetExportError(`지원하지 않는 형식이에요: ${format}`, { code: 'unsupported_format' });
  }
  if (!outline || typeof outline !== 'object') {
    throw new WorksheetExportError('학습지 내용이 없어요', { code: 'missing_outline' });
  }

  const resolved = resolveWorksheetExportLayout(outline);
  const exportColumns = columns === 2 || columns === 1 ? columns : resolved.columns;
  const exportPaper = paperStyle === 'school' || paperStyle === 'csat'
    ? paperStyle
    : resolved.paperStyle;
  const body = {
    outline,
    format: normalizedFormat,
    include_answer_guide: Boolean(includeAnswerGuide),
    columns: exportColumns,
    filename: filename || null,
    conversation_id: conversationId || null,
    outline_version_id: outlineVersionId || null,
    canvas_version_id: canvasVersionId || null,
    preview_pages: Math.max(0, Math.min(3, Number(previewPages) || 0)),
  };
  if (exportPaper) body.paper_style = exportPaper;

  const response = await fetchWithRetry(
    `${getApiBaseUrl()}/api/worksheet/export`,
    { method: 'POST', headers: await buildHeaders(), body: JSON.stringify(body), signal },
    { maxRetries: 0, timeoutMs: calcApiTimeout('default') },
  );

  let data = null;
  try { data = await response.json(); } catch (_e) { data = null; }

  if (!response.ok) {
    const detail = data?.detail?.message || data?.detail || data?.message;
    throw new WorksheetExportError(
      messageForStatus(response.status, typeof detail === 'string' ? detail : null),
      { status: response.status, code: data?.detail?.code || data?.code || null },
    );
  }
  if (!data?.success || !data.downloadUrl) {
    throw new WorksheetExportError('파일 주소를 받지 못했어요. 다시 시도해주세요', { status: response.status });
  }

  const record = buildArtifactRecord({
    kind: 'worksheet',
    format: data.format || normalizedFormat,
    filename: data.filename || `${outline.title || '학습지'}.${normalizedFormat}`,
    downloadUrl: data.downloadUrl,
    pageCount: Number.isInteger(data.pageCount) && data.pageCount >= 0 ? data.pageCount : 0,
    previewUrls: Array.isArray(data.previewUrls) ? data.previewUrls : [],
    sourceMode: data.sourceMode || 'text',
    canvasVersionId: data.canvasVersionId || canvasVersionId || null,
    outlineVersionId: data.outlineVersionId || outlineVersionId || null,
    createdAt: data.createdAt || undefined,
    sizeBytes: Number.isInteger(data.sizeBytes) ? data.sizeBytes : undefined,
    // 로그인 사용자 hwpx 만 값이 있다 — 우측 패널(rhwp 편집기) 오픈용
    artifactId: data.artifactId || undefined,
    editorPath: data.editorPath || undefined,
    // 단 수(1|2) — 2단은 rhwp 패널에서 한 줄로 보이므로 안내에 쓴다
    columns: data.columns ?? exportColumns,
  });

  return { record, raw: data };
}

const worksheetExportApi = {
  WORKSHEET_EXPORT_FORMATS,
  WorksheetExportError,
  exportWorksheet,
  isWorksheetExportAvailable,
  resolveWorksheetExportLayout,
};

export default worksheetExportApi;
